import { Minus, Plus, CheckCircle2, Circle, Trash2, TrendingUp, TrendingDown, Users } from "lucide-react";
import { useState } from "react";
import { brl, num, numStr, parseNum, pct } from "@/lib/format";
import EntriesDialog from "@/components/conference/EntriesDialog";

function QtyInput({ label, value, onChange, disabled, testid }) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="caption">{label}</span>
      <input className="input input-sm tnum text-right" inputMode="decimal" disabled={disabled} value={value}
        onChange={(e) => onChange(e.target.value)} onFocus={(e) => e.target.select()} data-testid={testid} />
    </label>
  );
}

function PurchasesInput({ it, value, onChange, disabled, canAdd, onOpen }) {
  const n = (it.purchase_entries || []).length;
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="caption">Compras</span>
      <div className="relative">
        <input className={`input input-sm tnum text-right ${canAdd || n > 0 ? "!pl-9" : ""}`} inputMode="decimal" disabled={disabled} value={value}
          onChange={(e) => onChange(e.target.value)} onFocus={(e) => e.target.select()} data-testid={`item-purchases-${it.product_id}`} />
        {(canAdd || n > 0) && (
          <button type="button" className="absolute left-1 top-1/2 -translate-y-1/2 h-7 min-w-7 px-1 rounded-md flex items-center justify-center gap-0.5 text-[11px] font-bold"
            style={{ background: "var(--brand-soft)", color: "var(--brand)" }} onClick={onOpen} title="Lançamentos de compra" data-testid={`item-add-purchase-${it.product_id}`}>
            {n > 0 ? <span data-testid={`item-purchases-count-${it.product_id}`}>{n}</span> : <Plus size={13} />}
          </button>
        )}
      </div>
    </div>
  );
}

function ItemHeader({ it, counted, editable, onToggleCounted }) {
  const label = counted ? "Contado" : "Pendente";
  const title = counted ? (it.counted_by ? `Contado por ${it.counted_by}` : "Contado") : "Pendente · toque para confirmar";
  return (
    <div className="flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="h3 truncate">{it.product_name}</div>
        <div className="caption">{it.category_name || "Sem categoria"} · {brl(it.unit_cost)}/{it.unit}{it.sector_name ? ` · Setor: ${it.sector_name}` : ""}</div>
      </div>
      <button className="flex items-center gap-1.5 text-xs font-semibold shrink-0" title={title} disabled={!editable} onClick={() => onToggleCounted(!counted)} data-testid={`item-counted-${it.product_id}`}
        style={{ color: counted ? "var(--success)" : "var(--muted)" }}>
        {counted ? <CheckCircle2 size={18} /> : <Circle size={18} />}
        <span className="hidden sm:inline">{label}</span>
      </button>
    </div>
  );
}

function ItemBadges({ it }) {
  if (!(it.loss_qty > 0 || it.divergent)) return null;
  const up = it.divergence_pct > 0;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {it.loss_qty > 0 && <span className="badge badge-danger" data-testid={`item-loss-${it.product_id}`}><Trash2 size={11} /> Perda: {num(it.loss_qty)} {it.unit}</span>}
      {it.divergent && (
        <span className="badge badge-warning" data-testid={`item-divergence-${it.product_id}`}>
          {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
          Fora do normal: média {num(it.final_avg)} ({up ? "+" : "−"}{pct(Math.abs(it.divergence_pct), 0)})
        </span>
      )}
    </div>
  );
}

function CountsButton({ it, editable, onOpen }) {
  const entries = it.count_entries || [];
  if (!editable && !entries.length) return null;
  const people = new Set(entries.map((e) => e.user_name)).size;
  const title = entries.length ? `${entries.length} contagem(ns) parcial(is) · ${people} pessoa(s)` : "Somar contagem parcial (vários usuários/setores)";
  return (
    <button type="button" className="ml-auto h-6 px-1.5 rounded-md flex items-center gap-1 text-[11px] font-bold whitespace-nowrap shrink-0" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}
      onClick={onOpen} title={title} aria-label={title} data-testid={`item-add-count-${it.product_id}`}>
      {editable && <Plus size={12} />}
      {entries.length > 0 && <span data-testid={`item-counts-count-${it.product_id}`}>{entries.length}</span>}
      {people > 1 && <Users size={11} />}
    </button>
  );
}

function FinalInput({ it, value, counted, editable, onChange, onOpen }) {
  const pid = it.product_id;
  const step = (d) => onChange(numStr(Math.max(0, Math.round((parseNum(value) + d) * 1000) / 1000)));
  return (
    <div className="flex flex-col gap-1">
      <span className="caption flex items-center gap-2 min-w-0"><span className="truncate">Final (contagem)</span><CountsButton it={it} editable={editable} onOpen={onOpen} /></span>
      <div className="flex gap-1.5">
        <button className="stepper" disabled={!editable} onClick={() => step(-1)} data-testid={`item-minus-${pid}`}><Minus size={15} /></button>
        <input className="input input-sm tnum text-center flex-1 min-w-0" inputMode="decimal" disabled={!editable} value={value}
          style={{ borderColor: counted ? "var(--success)" : undefined }} onFocus={(e) => e.target.select()}
          onChange={(e) => onChange(e.target.value)} data-testid={`item-final-${pid}`} />
        <button className="stepper" disabled={!editable} onClick={() => step(1)} data-testid={`item-plus-${pid}`}><Plus size={15} /></button>
      </div>
    </div>
  );
}

export default function ItemRow({ it, draft, onChange, onToggleCounted, editable, finalOnly, hidePurchases, confId, onConfUpdated }) {
  const pid = it.product_id;
  const [entries, setEntries] = useState(null);
  const val = (f) => (draft[f] !== undefined ? draft[f] : String(it[f] ?? 0).replace(".", ","));
  const counted = draft.counted !== undefined ? draft.counted : it.counted;
  const qtyLocked = !editable || finalOnly;
  const consumption = parseNum(val("initial_qty")) + (hidePurchases ? 0 : parseNum(val("purchases_qty"))) - parseNum(val("final_qty"));
  return (
    <div className="card min-w-0" style={{ padding: 14, borderColor: it.divergent ? "var(--accent)" : undefined }} data-testid={`conf-item-${pid}`}>
      <ItemHeader it={it} counted={counted} editable={editable} onToggleCounted={onToggleCounted} />
      <ItemBadges it={it} />
      <div className="grid grid-cols-2 gap-3 mt-3 items-end">
        <QtyInput label="Inicial" value={val("initial_qty")} disabled={qtyLocked} onChange={(v) => onChange("initial_qty", v)} testid={`item-initial-${pid}`} />
        {!hidePurchases && <PurchasesInput it={it} value={val("purchases_qty")} disabled={qtyLocked} canAdd={!qtyLocked} onChange={(v) => onChange("purchases_qty", v)} onOpen={() => setEntries("purchases")} />}
        <div className={hidePurchases ? "" : "col-span-2"}><FinalInput it={it} value={val("final_qty")} counted={counted} editable={editable} onChange={(v) => onChange("final_qty", v)} onOpen={() => setEntries("counts")} /></div>
      </div>
      {entries && (
        <EntriesDialog kind={entries} open onOpenChange={(o) => !o && setEntries(null)} confId={confId} it={it}
          editable={entries === "counts" ? editable : !qtyLocked} onUpdated={(d) => onConfUpdated(d, entries)}
          hasDraft={draft[entries === "counts" ? "final_qty" : "purchases_qty"] !== undefined} />
      )}
      <div className="flex justify-between mt-3 caption">
        <span>{hidePurchases ? "Variação estoque" : "Consumo"}: <b className="tnum" style={{ color: "var(--text)" }}>{num(consumption)} {it.unit}</b></span>
        <span className="tnum">{brl(consumption * it.unit_cost)}</span>
      </div>
    </div>
  );
}
